import { useEffect, useState } from 'react'
import { Badge, BlockStack, Button, Card, Divider, InlineStack, Modal, Spinner, Text } from '@shopify/polaris'
import { healthApi, type CatalogAuditResult, type CatalogIssue, type CategoryReport } from '../../../../sdk/health.api'
import { getShop } from '../../../../sdk/shop'

const SEVERITY_TONE: Record<string, React.ComponentProps<typeof Badge>['tone']> = {
  critical: 'critical',
  warning:  'warning',
}

const CODE_LABEL: Record<string, string> = {
  missing_sku:          'No SKU',
  missing_cost:         'No Cost',
  missing_vendor:       'No Vendor',
  untracked_inventory:  'Untracked',
  inactive_product:     'Inactive',
}

function CatalogIssueGroup({ code, issues }: { code: string; issues: CatalogIssue[] }) {
  const [open, setOpen] = useState(false)
  const first = issues[0]!
  return (
    <>
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="150" blockAlign="center">
          <Badge tone={SEVERITY_TONE[first.severity]}>{CODE_LABEL[code] ?? code}</Badge>
          <Text as="span" variant="bodySm">{first.title}</Text>
        </InlineStack>
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" variant="bodySm" tone="subdued">{issues.length} product{issues.length !== 1 ? 's' : ''}</Text>
          <Button variant="plain" size="slim" onClick={() => setOpen(true)}>View</Button>
        </InlineStack>
      </InlineStack>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={first.title}
        secondaryActions={[{ content: 'Close', onAction: () => setOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" variant="bodySm" tone="subdued">{first.description}</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Fix these products on Shopify admin, then refresh System Health.
            </Text>
            <div style={{ border: '1px solid var(--p-color-border-subdued)', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: '#f6f6f7' }}>
                  <tr>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6d7175' }}>Product</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6d7175' }}>Variant</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6d7175' }}>SKU</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map(issue => (
                    <tr key={issue.variantId} style={{ borderTop: '1px solid #f1f2f3' }}>
                      <td style={{ padding: '6px 12px', fontWeight: 600 }}>{issue.productTitle}</td>
                      <td style={{ padding: '6px 12px', color: '#6d7175' }}>{issue.displayName}</td>
                      <td style={{ padding: '6px 12px', fontFamily: 'monospace', color: '#6d7175' }}>{issue.sku ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  )
}

type Props = {
  cat?:         CategoryReport
  onViewIssues: (validatorId: string) => void
  refreshKey?:  number
}

export function ProductsHealthCard({ cat, onViewIssues, refreshKey }: Props) {
  const shop = getShop() ?? ''
  const [catalog,  setCatalog]  = useState<CatalogAuditResult | null>(null)
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState<string | null>(null)

  function load() {
    setLoading(true)
    setError(null)
    healthApi.getCatalogAudit(shop)
      .then(setCatalog)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { if (shop) load() }, [shop, refreshKey])

  // Group Shopify catalog issues by code
  const grouped = catalog
    ? catalog.issues.reduce<Record<string, CatalogIssue[]>>((acc, issue) => {
        ;(acc[issue.code] ??= []).push(issue)
        return acc
      }, {})
    : {}

  // missing_sku always renders (health indicator even when 0)
  const skuIssues  = grouped['missing_sku'] ?? []
  const otherCodes = Object.entries(grouped).filter(([code]) => code !== 'missing_sku')

  // Status badge for the whole card
  const catalogCritical = catalog?.criticalCount ?? 0
  const catalogWarning  = catalog?.warningCount  ?? 0
  const sheetCritical   = cat?.criticalCount ?? 0
  const sheetWarning    = cat?.issueCount ?? 0 - sheetCritical

  const totalCritical = catalogCritical + sheetCritical
  const totalWarning  = catalogWarning  + (sheetWarning > 0 ? sheetWarning : 0)

  const statusTone: React.ComponentProps<typeof Badge>['tone'] =
    totalCritical > 0 ? 'critical'
    : totalWarning  > 0 ? 'warning'
    : (loading && !catalog) ? 'new'
    : 'success'

  const statusLabel =
    totalCritical > 0 ? `${totalCritical} critical`
    : totalWarning  > 0 ? `${totalWarning} warnings`
    : (loading && !catalog) ? 'Loading'
    : 'All clear'

  // Sheet-side validators for products (from CategoryReport)
  const sheetValidators = cat?.validators ?? []

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingSm">Products Health</Text>
              <Badge tone={statusTone}>{statusLabel}</Badge>
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">
              Product data quality on Shopify and in order records
            </Text>
          </BlockStack>
        </InlineStack>

        {/* ── On Shopify ───────────────────────────────────────────────── */}
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Text as="p" variant="bodySm" fontWeight="semibold">On Shopify</Text>
            {catalog && (
              <Text as="span" variant="bodySm" tone="subdued">
                {catalog.totalVariants} total · {catalog.activeVariants} active
              </Text>
            )}
          </InlineStack>

          {loading && !catalog && (
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" accessibilityLabel="Scanning catalog" />
              <Text as="p" variant="bodySm" tone="subdued">Scanning Shopify catalog…</Text>
            </InlineStack>
          )}

          {error && <Text as="p" variant="bodySm" tone="critical">{error}</Text>}

          {catalog && (
            <BlockStack gap="200">
              {skuIssues.length > 0
                ? <CatalogIssueGroup code="missing_sku" issues={skuIssues} />
                : (
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="150" blockAlign="center">
                      <Badge tone="success">No SKU</Badge>
                      <Text as="span" variant="bodySm">Missing SKU</Text>
                    </InlineStack>
                    <Text as="span" variant="bodySm" tone="subdued">0 products</Text>
                  </InlineStack>
                )
              }
              {otherCodes.length > 0
                ? otherCodes.map(([code, issues]) => (
                    <CatalogIssueGroup key={code} code={code} issues={issues} />
                  ))
                : <Text as="p" variant="bodySm" tone="subdued">No other catalog issues found.</Text>
              }
            </BlockStack>
          )}
        </BlockStack>

        <Divider />

        {/* ── In Orders Sheet ──────────────────────────────────────────── */}
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Text as="p" variant="bodySm" fontWeight="semibold">In Orders Sheet</Text>
            {cat && (
              <Text as="span" variant="bodySm" tone="subdued">
                {cat.totalRows} SKUs tracked
              </Text>
            )}
          </InlineStack>

          {!cat || cat.status === 'not_configured' ? (
            <Text as="p" variant="bodySm" tone="subdued">No order data yet.</Text>
          ) : cat.issueCount === 0 ? (
            <Text as="p" variant="bodySm" tone="subdued">All order line items have complete product data.</Text>
          ) : (
            <BlockStack gap="100">
              {sheetValidators.map(v => (
                <InlineStack key={v.validatorId} align="space-between" blockAlign="center">
                  <InlineStack gap="150" blockAlign="center">
                    <Badge tone={v.severity === 'critical' ? 'critical' : 'warning'}>
                      {`${v.affectedCount} ${v.severity === 'critical' ? 'critical' : 'warning'}`}
                    </Badge>
                    <Text as="span" variant="bodySm">{v.title}</Text>
                  </InlineStack>
                  {v.affectedCount > 0 && (
                    <Button variant="plain" size="slim" onClick={() => onViewIssues(v.validatorId)}>Fix</Button>
                  )}
                </InlineStack>
              ))}
            </BlockStack>
          )}
        </BlockStack>
      </BlockStack>
    </Card>
  )
}
